/**
 * Torg Pricing in the PDP buy box (contract §7; legal display rules in
 * .docs/torg-search-migration/22-dfp-forpackning.md §Juridik).
 *
 * The server renders the block for the shown product
 * (versa_torg_pricing_buy_box() in app/helpers/pdp-buy-box.php — keep the
 * markup below in step with it). This module
 * - re-renders it for the chosen variation (found_variation →
 *   variation.versa_torg_tiers, falling back to the plugin's torg_tiers);
 * - re-renders it on edge-state hydration (window.VersaTorgPricing.hydrate,
 *   called by mu-plugins/versa-pdp-edge/assets/pdp-state.js);
 * - keeps the price row, the stepper and the CTA in step with the chosen pack:
 *   price row = pack total + grey "79 kr/st" (never red, no struck price),
 *   stepper = packs (min 1, max whole packs in stock),
 *   CTA = data-quantity packs × N and data-torg_pack N (add-to-cart.js posts both).
 *
 * "1 st" is always preselected; a new variation or a vanished pack falls back to it.
 */

const ROOT = '[data-versa-torg-pricing]';
const CHIP_BASE = 'relative cursor-pointer rounded-full bg-white px-3 py-1.5 text-sm font-medium text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-versa-700';
const CHIP_ON = ['ring-2', 'ring-gray-900'];
const CHIP_OFF = ['ring-1', 'ring-gray-200', 'hover:ring-gray-400'];

const states = new WeakMap();

function stateOf(root) {
    let state = states.get(root);
    if (!state) {
        // pack: chosen pack size (1 = single units); base: the price row before a
        // pack took it over; orig: the stepper's own min/max before pack mode.
        state = { pack: 1, base: null, orig: null };
        states.set(root, state);
    }
    return state;
}

function esc(value) {
    return String(value === undefined || value === null ? '' : value).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

/** wc_price() for tiers that arrive without price HTML (plugin's raw torg_tiers). */
function formatPrice(root, amount) {
    let cfg = {};
    try {
        cfg = JSON.parse(root.dataset.versaTorgCurrency || '{}') || {};
    } catch (e) {
        cfg = {};
    }
    const decimals = Number.isInteger(cfg.decimals) ? cfg.decimals : 2;
    const fixed = Number(amount).toFixed(decimals).split('.');
    const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, cfg.thousand_separator || '');
    let number = whole;
    if (fixed[1] && !(cfg.trim_zeros && /^0+$/.test(fixed[1]))) {
        number = whole + (cfg.decimal_separator || ',') + fixed[1];
    }
    const symbol = '<span class="woocommerce-Price-currencySymbol">' + esc(cfg.symbol || '') + '</span>';
    const format = cfg.format || '%2$s&nbsp;%1$s';
    return '<span class="woocommerce-Price-amount amount"><bdi>' + format.replace('%1$s', symbol).replace('%2$s', number) + '</bdi></span>';
}

/**
 * Tiers in the shape the markup needs. Theme-enriched tiers (versa_torg_tiers,
 * PHP versa_torg_pricing_tiers()) carry price HTML and stock limits; the
 * plugin's raw torg_tiers are filtered here the same way (below the current
 * price, packs only when the stock covers one).
 */
function normaliseTiers(root, list, limits) {
    limits = limits || {};
    const maxQty = Number(limits.maxQty) > 0 ? Number(limits.maxQty) : -1;
    const current = Number(limits.displayPrice) || 0;
    return (Array.isArray(list) ? list : []).map((tier) => {
        if (!tier || (tier.mode !== 'pack' && tier.mode !== 'quantity')) return null;
        const min = parseInt(tier.min_qty, 10);
        const unit = Number(tier.unit_price_display);
        if (!(min >= 2) || !(unit > 0)) return null;
        const enriched = tier.unit_html !== undefined;
        if (!enriched && current > 0 && unit >= current - 0.004) return null;
        let maxPacks = 0;
        if (tier.mode === 'pack') {
            maxPacks = enriched ? parseInt(tier.max_packs, 10) : (maxQty > 0 ? Math.floor(maxQty / min) : -1);
            if (isNaN(maxPacks)) maxPacks = -1;
            if (maxPacks === 0) return null;
        }
        const total = Number(tier.total_display) > 0 ? Number(tier.total_display) : unit * min;
        return {
            mode: tier.mode,
            min_qty: min,
            unit_price: unit,
            unit_html: enriched ? tier.unit_html : formatPrice(root, unit),
            total_html: tier.total_html !== undefined ? tier.total_html : formatPrice(root, total),
            max_packs: maxPacks,
            available: enriched ? tier.available !== false : (maxQty < 0 || maxQty >= min),
        };
    }).filter(Boolean).sort((a, b) => (a.mode === b.mode ? a.min_qty - b.min_qty : (a.mode === 'pack' ? -1 : 1)));
}

function chipHtml(pack, label, tier) {
    const on = pack === 1;
    const data = tier
        ? ` data-torg-total-html="${esc(tier.total_html)}" data-torg-unit-html="${esc(tier.unit_html)}" data-torg-unit-price="${esc(tier.unit_price)}" data-torg-max-packs="${tier.max_packs}"`
        : '';
    return `<button type="button" role="radio" aria-checked="${on}" tabindex="${on ? 0 : -1}" data-versa-torg-pack-option="${pack}"${data} class="${CHIP_BASE} ${(on ? CHIP_ON : CHIP_OFF).join(' ')}"><span class="pointer-events-none">${esc(label)}</span></button>`;
}

function rowHtml(tier, ctx) {
    const qty = tier.min_qty;
    return `<li class="flex items-center gap-3 px-3 py-2 text-sm">
        <span class="w-14 shrink-0 text-gray-600">${qty}+ st</span>
        <span class="flex-1 min-w-0"><span class="font-semibold text-gray-900">${tier.unit_html}</span><span class="text-gray-500">/st</span></span>
        <button type="button" data-quantity="${qty}" data-product_id="${esc(ctx.productId)}" data-variation_id="${esc(ctx.variationId || 0)}" data-product_name="${esc(ctx.productName)}" aria-label="${esc('Lägg ' + qty + ' st i varukorgen')}"${tier.available ? '' : ' disabled'}
            class="add_to_cart_button versa-tier-add h-8 shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md border border-versa-700 bg-white px-2.5 text-sm font-medium text-versa-700 hover:bg-versa-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-versa-700 disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white">
            <svg class="add_to_cart_button_initial h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
            <svg class="hidden add_to_cart_button_loading animate-spin h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
            <svg class="hidden add_to_cart_button_added h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
            <span class="add_to_cart_button_label pointer-events-none">${qty} st</span>
            <span class="add_to_cart_button_label_adding hidden pointer-events-none">${qty} st</span>
            <span class="add_to_cart_button_label_added hidden pointer-events-none">${qty} st</span>
        </button>
    </li>`;
}

/** Same markup as versa_torg_pricing_inner_html(). */
function render(root, tiers, variationId) {
    const ctx = {
        productId: root.dataset.productId || '',
        variationId: variationId || 0,
        productName: root.dataset.productName || '',
    };
    const packs = tiers.filter((t) => t.mode === 'pack');
    const quantities = tiers.filter((t) => t.mode === 'quantity');
    const labelId = 'versa-torg-pack-label-' + ctx.productId;
    let html = '';
    if (packs.length) {
        html += `<div data-versa-torg-pack><p class="m-0 mb-2 text-sm text-gray-700" id="${esc(labelId)}">Förpackning</p>`
            + `<div class="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="${esc(labelId)}">`
            + chipHtml(1, '1 st')
            + packs.map((t) => chipHtml(t.min_qty, t.min_qty + '-pack', t)).join('')
            + '</div></div>';
    }
    if (quantities.length) {
        html += '<div class="versa-tiers" data-versa-torg-tiers><p class="m-0 mb-2 text-sm text-gray-900">Antal</p>'
            + '<ul class="m-0 p-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200">'
            + quantities.map((t) => rowHtml(t, ctx)).join('')
            + '</ul></div>';
    }
    root.dataset.variationId = String(ctx.variationId);
    root.innerHTML = html;
    root.hidden = html === '';
    if (quantities.length && typeof window.observeAddToCartButtons === 'function') {
        window.observeAddToCartButtons(false);
    }
}

function ctaOf(root) {
    return document.getElementById('add_to_cart_' + root.dataset.productId)
        || (root.closest('form.cart') && root.closest('form.cart').querySelector('.single_add_to_cart_button'));
}

function quantityInputOf(root) {
    const form = root.closest('form.cart') || document.querySelector('form.cart');
    return form ? form.querySelector('input[name="quantity"]') : null;
}

function priceRow() {
    const box = document.querySelector('[data-versa-buy-price]');
    if (!box) return null;
    return {
        price: box.querySelector('[data-versa-pdp-price]'),
        regular: box.querySelector('[data-versa-pdp-regular]'),
        regularAmount: box.querySelector('[data-versa-pdp-regular-amount]'),
        unit: box.querySelector('[data-versa-pdp-unit]'),
    };
}

/** Pack mode: stepper counts packs. "1 st": the stepper's own limits again. */
function applyStepper(root, chip, reset) {
    const input = quantityInputOf(root);
    if (!input) return;
    const state = stateOf(root);
    if (state.pack > 1 && chip) {
        if (!state.orig) {
            state.orig = { min: input.getAttribute('min'), max: input.getAttribute('max') };
        }
        let maxPacks = parseInt(chip.dataset.torgMaxPacks, 10);
        maxPacks = maxPacks > 0 ? maxPacks : Infinity;
        const origMax = parseInt(state.orig.max, 10);
        if (origMax > 0) maxPacks = Math.min(maxPacks, Math.max(1, Math.floor(origMax / state.pack)));
        input.setAttribute('min', '1');
        if (maxPacks === Infinity) input.removeAttribute('max');
        else input.setAttribute('max', String(maxPacks));
        let value = reset ? 1 : (parseInt(input.value, 10) || 1);
        value = Math.max(1, Math.min(value, maxPacks));
        input.value = String(value);
    } else {
        if (state.orig) {
            ['min', 'max'].forEach((attr) => {
                if (state.orig[attr] === null || state.orig[attr] === '') input.removeAttribute(attr);
                else input.setAttribute(attr, state.orig[attr]);
            });
            state.orig = null;
        }
        if (reset) input.value = '1';
    }
}

function applyCta(root) {
    const button = ctaOf(root);
    if (!button) return;
    const state = stateOf(root);
    const input = quantityInputOf(root);
    const count = input ? (parseInt(input.value, 10) || 0) : 1;
    button.dataset.torg_pack = state.pack > 1 ? String(state.pack) : '0';
    button.setAttribute('data-quantity', String(state.pack > 1 ? Math.max(1, count) * state.pack : Math.max(1, count)));
}

/** Price row: pack total + grey unit price, or the product's own price again. */
function applyPrice(root, chip) {
    const row = priceRow();
    if (!row || !row.price) return;
    const state = stateOf(root);
    if (state.pack > 1 && chip) {
        if (!state.base) {
            state.base = {
                html: row.price.innerHTML,
                sale: row.price.classList.contains('text-red-600'),
                regularHidden: row.regular ? row.regular.hidden : true,
            };
        }
        row.price.innerHTML = chip.dataset.torgTotalHtml || '';
        // A pack price is never a reduced price: no red, no struck regular price.
        row.price.classList.remove('text-red-600');
        row.price.classList.add('text-versa-700');
        if (row.regular) row.regular.hidden = true;
        if (row.unit) {
            row.unit.innerHTML = (chip.dataset.torgUnitHtml || '') + '/st';
            row.unit.hidden = false;
        }
        return;
    }
    if (state.base) {
        row.price.innerHTML = state.base.html;
        row.price.classList.toggle('text-red-600', !!state.base.sale);
        row.price.classList.toggle('text-versa-700', !state.base.sale);
        if (row.regularAmount && state.base.regularHtml !== undefined) row.regularAmount.innerHTML = state.base.regularHtml;
        if (row.regular) row.regular.hidden = !!state.base.regularHidden;
        state.base = null;
    }
    if (row.unit) {
        row.unit.hidden = true;
        row.unit.innerHTML = '';
    }
}

function markChips(root, pack) {
    root.querySelectorAll('[data-versa-torg-pack-option]').forEach((el) => {
        const on = parseInt(el.dataset.versaTorgPackOption, 10) === pack;
        el.setAttribute('aria-checked', on ? 'true' : 'false');
        el.tabIndex = on ? 0 : -1;
        el.classList.remove(...CHIP_ON, ...CHIP_OFF);
        el.classList.add(...(on ? CHIP_ON : CHIP_OFF));
    });
}

/**
 * Choose a pack (1 = single units); a pack that is not offered falls back to 1.
 *
 * options.reset: reset the stepper to 1 (default: when the pack changes);
 * options.quiet: leave stepper, CTA and price row alone when staying on "1 st".
 */
function select(root, pack, options) {
    options = options || {};
    pack = parseInt(pack, 10) || 1;
    const chip = pack > 1 ? root.querySelector(`[data-versa-torg-pack-option="${pack}"]`) : null;
    if (!chip) pack = 1;
    const state = stateOf(root);
    const changed = state.pack !== pack;
    state.pack = pack;
    markChips(root, pack);
    if (options.quiet && !changed && pack === 1) return;
    applyStepper(root, chip, options.reset !== undefined ? options.reset : changed);
    applyCta(root);
    applyPrice(root, chip);
}

/** Variation picked: its tiers, "1 st" again, the variation's own price as the base. */
function onFoundVariation(event, variation) {
    const form = event && event.target && event.target.closest ? event.target.closest('form') : null;
    const root = (form && form.querySelector(ROOT)) || document.querySelector(ROOT);
    if (!root || !variation) return;
    const state = stateOf(root);
    // WooCommerce has just set the stepper's min/max for this variation.
    state.orig = null;
    state.base = variation.versa_price_html !== undefined
        ? {
            html: variation.versa_price_html,
            sale: !!variation.versa_sale,
            regularHtml: variation.versa_regular_html || '',
            regularHidden: !variation.versa_regular_html,
        }
        : null;
    const list = Array.isArray(variation.versa_torg_tiers) ? variation.versa_torg_tiers : variation.torg_tiers;
    const tiers = normaliseTiers(root, list, { maxQty: variation.max_qty, displayPrice: variation.display_price });
    render(root, tiers, variation.variation_id);
    select(root, 1, { quiet: true });
    state.base = null;
    toggleAwdrTable(tiers.some((t) => t.mode === 'quantity'));
}

function onVariationReset(event) {
    const form = event && event.target && event.target.closest ? event.target.closest('form') : null;
    const root = (form && form.querySelector(ROOT)) || document.querySelector(ROOT);
    if (!root) return;
    select(root, 1, { quiet: true });
    render(root, [], 0);
    toggleAwdrTable(false);
}

/** Never show the AWDR "Antal" table next to Torg quantity tiers. */
function toggleAwdrTable(hide) {
    document.querySelectorAll('[data-versa-awdr-tiers]').forEach((el) => {
        el.hidden = !!hide;
    });
}

function onKeydown(event) {
    const chip = event.target.closest && event.target.closest('[data-versa-torg-pack-option]');
    if (!chip) return;
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const root = chip.closest(ROOT);
    const chips = Array.from(root.querySelectorAll('[data-versa-torg-pack-option]'));
    const next = chips[(chips.indexOf(chip) + keys[event.key] + chips.length) % chips.length];
    select(root, next.dataset.versaTorgPackOption);
    next.focus();
}

function onClick(event) {
    const chip = event.target.closest && event.target.closest('[data-versa-torg-pack-option]');
    if (!chip) return;
    event.preventDefault();
    select(chip.closest(ROOT), chip.dataset.versaTorgPackOption);
}

/** Edge-state hydration (pdp-state.js): fresh tiers, keep the chosen pack while it is offered. */
function hydrate(product) {
    const root = document.querySelector(ROOT);
    if (!root || !product) return;
    const state = stateOf(root);
    const keep = state.pack;
    // pdp-state.js has just written the product's own price and stepper limits.
    state.base = null;
    state.orig = null;
    render(root, normaliseTiers(root, product.torg_tiers), 0);
    // Same pack still offered: keep the customer's pack count; otherwise single units again.
    select(root, keep, { quiet: true, reset: keep > 1 && !root.querySelector(`[data-versa-torg-pack-option="${keep}"]`) });
}

window.VersaTorgPricing = { hydrate, select };

document.addEventListener('click', onClick);
document.addEventListener('keydown', onKeydown);

if (window.jQuery) {
    window.jQuery(document)
        .on('found_variation', 'form.variations_form', onFoundVariation)
        .on('reset_data hide_variation', 'form.variations_form', onVariationReset);
}
